// EmptiesStrip — horizontal bar-top pile of completed drink glasses.
// Ported from design-canon/project/src/screens-logger.jsx lines 280-310.
//
// Each new empty glass animates in from above via the `gs-drop-in` keyframe
// (canon.css). Glasses overlap with a slight randomized tilt so the pile
// reads like a real bar top, not a grid.
//
// Mobile budget: a single horizontal scroll inside a 56px container, no
// nested scroll regions. The strip itself is the only scrollable element on
// this screen.

import type { ReactElement } from 'react'
import { useEffect, useRef } from 'react'
import type { DrinkDto } from '../api'
import { type DrinkType, MiniGlass } from './DrinkTile'

export type EmptiesStripProps = {
  drinks: DrinkDto[]
  onSeeBreakdown?: () => void
}

const DRINK_TYPES: ReadonlySet<DrinkType> = new Set(['beer', 'wine', 'cocktail', 'shot'])

function asDrinkType(value: string): DrinkType {
  return DRINK_TYPES.has(value as DrinkType) ? (value as DrinkType) : 'beer'
}

/** Deterministic-ish tilt per drink so the pile looks varied but stable. */
function tiltFor(id: string, fallbackIndex: number): number {
  // sin-of-hash gives a stable pseudo-random [-6,6] without crypto.
  let h = 0
  const src = id || String(fallbackIndex)
  for (let i = 0; i < src.length; i += 1) h = (h * 31 + src.charCodeAt(i)) | 0
  const seed = Math.abs(Math.sin(h * 0.91) * 1000)
  const t = (seed % 12) - 6
  return Math.round(t * 10) / 10
}

export function EmptiesStrip({ drinks, onSeeBreakdown }: EmptiesStripProps): ReactElement {
  const stripRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll to the newest empty whenever the count changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-scroll when the count changes; stripRef is a stable ref.
  useEffect(() => {
    const el = stripRef.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' })
    })
  }, [drinks.length])

  return (
    <div className="empties-strip">
      <div className="empties-bar" ref={stripRef} data-testid="empties-bar">
        {drinks.length === 0 ? (
          <span className="empties-hint">↑ first round on you?</span>
        ) : (
          drinks.map((d, i) => {
            const tilt = tiltFor(d.id, i)
            return (
              <span
                key={d.id}
                className="empty-item"
                style={{
                  marginLeft: i === 0 ? 0 : -10,
                  transform: `rotate(${tilt}deg)`,
                  zIndex: i,
                }}
              >
                <MiniGlass type={asDrinkType(d.drinkType)} size={22} />
              </span>
            )
          })
        )}
      </div>
      <button
        type="button"
        className="empties-count"
        onClick={onSeeBreakdown}
        disabled={!onSeeBreakdown}
        aria-label="See breakdown"
        data-testid="empties-count"
      >
        <div className="mono" style={{ fontSize: 22, fontWeight: 800 }}>
          {drinks.length}
        </div>
        <div className="eyebrow" style={{ fontSize: 9 }}>
          empties
        </div>
      </button>
    </div>
  )
}
